import { useWizard } from 'react-use-wizard';
import type { Survey, SurveyQuestionType } from 'posthog-js';

import Button, { BUTTON_TYPE } from '../Button';
import { icons, IconText } from '../Icon';

import type { QuestionRenderer } from './questions/registry';

import styles from './QuestionStep.module.less';

type Answer = string | string[];

type Props = {
  /** PostHog survey question shape (open / link / rating / single / multiple). */
  question: Survey['questions'][number];
  /** Current answer for this question. `undefined` for unanswered. */
  value: Answer | undefined;
  /** Called when the renderer reports a new answer. */
  onChange: (value: Answer) => void;
  /**
   * Optional async hook awaited before advancing. `SurveyWizard` wires this on
   * the last question step to fire `posthog.capture('survey sent', ...)` before
   * `nextStep()` runs.
   */
  onSubmit?: () => void | Promise<void>;
  /** Map of `SurveyQuestionType` to the component used to render each type. */
  renderers: Partial<Record<SurveyQuestionType, QuestionRenderer>>;
};

const isAnswered = (value: Answer | undefined) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== undefined && value !== '';
};

/**
 * One step in a `SurveyWizard`. Looks up the matching renderer in `renderers`
 * by the question's type, renders it, and provides Back/Next navigation. Next
 * is disabled until the question has a non-empty answer (or is `optional`).
 * Falls back to a "not supported yet" placeholder when no renderer is
 * registered for the question's type.
 */
export const QuestionStep = ({ question, value, onChange, onSubmit, renderers }: Props) => {
  const { nextStep, previousStep, isFirstStep } = useWizard();
  const Renderer = renderers[question.type];
  const canAdvance = !!question.optional || isAnswered(value);

  const handleNext = async () => {
    if (onSubmit) {
      await onSubmit();
    }
    await nextStep();
  };

  if (!Renderer) {
    return (
      <div className={styles.step}>
        <p>This question type isn&apos;t supported yet.</p>
        <div className={styles.actions}>
          <Button onClick={handleNext} type={BUTTON_TYPE.SECONDARY}>
            <IconText icon={icons.angleRight} text="Next" alignIcon="right" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.questionContainer}>
      <div className={styles.step}>
        <Renderer question={question} value={value} onChange={onChange} />
      </div>
      <div className={styles.actions}>
        {!isFirstStep && (
          <Button onClick={previousStep} type={BUTTON_TYPE.SECONDARY}>
            <IconText icon={icons.angleLeft} text="Back" alignIcon="left" />
          </Button>
        )}
        <Button onClick={handleNext} isDisabled={!canAdvance}>
          <IconText icon={icons.angleRight} text={question.buttonText ?? 'Next'} alignIcon="right" />
        </Button>
      </div>
    </div>
  );
};
